import React, { useEffect, useMemo, useRef } from 'react';
import useWorkflowChat from './hooks/useWorkflowChat';
import ChatMessage from './components/ChatMessage';
import ChatInput from './components/ChatInput';
import TypingIndicator from './components/TypingIndicator';
import WorkflowPreviewPanel from './components/WorkflowPreviewPanel';
import { WorkflowPreviewBlock } from './types';
import { User } from 'common/components/UserAvatar/UserAvatar';
import BEM from 'common/bem';
import mobxInjectSelect from 'common/utils/mobxInjectSelect';

const classes = BEM.with('ClinicalWorkflows');

const EVENT_TYPES = [
  { value: 'Nurse Call', label: 'Nurse Call', description: 'Nurse call alarm workflows' },
  { value: 'ADT', label: 'ADT', description: 'Admit, Discharge, Transfer events' },
  {
    value: 'Infusion Pumps',
    label: 'Infusion Pumps',
    description: 'Infusion pump alert workflows',
  },
  {
    value: 'Medication Dispensing',
    label: 'Medication Dispensing',
    description: 'Medication dispensing events',
  },
  {
    value: 'Patient Monitor Alarms',
    label: 'Patient Monitor Alarms',
    description: 'Patient monitor alarm workflows',
  },
  { value: 'TigerConnect', label: 'TigerConnect', description: 'TigerConnect messaging events' },
  { value: 'Orders', label: 'Orders', description: 'Clinical order events' },
  { value: 'Results', label: 'Results', description: 'Lab and test result events' },
  { value: 'Custom', label: 'Custom', description: 'Describe your own workflow' },
];

type ClinicalWorkflowsProps = {};

type MobxProps = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: Record<string, any> | null;
  currentOrganization: { displayName: string; name: string } | null;
  currentOrganizationId: string;
  currentUser: User | null;
};

type CombinedProps = ClinicalWorkflowsProps & MobxProps;

function ClinicalWorkflows({
  client,
  currentOrganization,
  currentOrganizationId,
  currentUser,
}: CombinedProps) {
  const messageListRef = useRef<HTMLDivElement>(null);

  const orgContext = useMemo(
    () =>
      currentOrganization
        ? { orgName: currentOrganization.displayName, orgToken: currentOrganizationId }
        : null,
    [currentOrganization, currentOrganizationId]
  );

  const { messages, isLoading, error, sendMessage, selectOption } = useWorkflowChat(
    client,
    orgContext
  );

  const latestPreview = useMemo<WorkflowPreviewBlock | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.role !== 'assistant') continue;
      for (let j = msg.segments.length - 1; j >= 0; j--) {
        const seg = msg.segments[j];
        if (seg.kind === 'workflow_preview') {
          return seg.data;
        }
      }
    }
    return null;
  }, [messages]);

  useEffect(() => {
    const el = messageListRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages]);

  const lastAssistantIdx = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return i;
    }
    return -1;
  })();

  const hasUserRepliedToLast =
    lastAssistantIdx >= 0 && messages.slice(lastAssistantIdx + 1).some((m) => m.role === 'user');

  const showWelcome = messages.length === 0 && !isLoading;

  const handleEventSelect = (eventType: string) => {
    sendMessage(`Configure ${eventType} workflow`);
  };

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <div className={classes('header-icon')}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
          </svg>
        </div>
        <div className={classes('header-text')}>
          <h2 className={classes('title')}>Build Workflow</h2>
          <p className={classes('subtitle')}>Describe your automation in natural language</p>
        </div>
      </div>

      <div className={classes('split')}>
        <div className={classes('chat-pane')}>
          <div className={classes('message-list')} ref={messageListRef}>
            {showWelcome && (
              <div className={classes('welcome')}>
                <div className={classes('welcome-icon')}>
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                </div>
                <h3 className={classes('welcome-title')}>
                  What type of event would you like to configure?
                </h3>
                <p className={classes('welcome-subtitle')}>
                  Select an event type to get started with your workflow configuration.
                </p>
                <div className={classes('welcome-grid')}>
                  {EVENT_TYPES.map((evt) => (
                    <button
                      key={evt.value}
                      className={classes('welcome-btn')}
                      onClick={() => handleEventSelect(evt.value)}
                      disabled={!client}
                      type="button"
                    >
                      <span className={classes('welcome-btn-label')}>{evt.label}</span>
                      <span className={classes('welcome-btn-desc')}>{evt.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((msg, idx) => (
              <ChatMessage
                key={msg.id}
                message={msg}
                currentUser={currentUser ?? undefined}
                onSelectOption={selectOption}
                isLastAssistantMessage={idx === lastAssistantIdx}
                hasUserReplied={idx === lastAssistantIdx && hasUserRepliedToLast}
              />
            ))}
            {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
              <TypingIndicator />
            )}
          </div>

          {error && !isLoading && (
            <div className={classes('error-bar')}>
              <span>{error}</span>
            </div>
          )}

          <ChatInput onSend={sendMessage} disabled={isLoading || !client} />
        </div>

        <div className={classes('preview-pane')}>
          <WorkflowPreviewPanel preview={latestPreview} />
        </div>
      </div>
    </div>
  );
}

export default mobxInjectSelect<ClinicalWorkflowsProps, MobxProps>({
  messengerStore: ['client', 'currentOrganization', 'currentOrganizationId'],
  sessionStore: ['currentUser'],
})(ClinicalWorkflows);
